import * as React from "react";
import type InfiniteLoader from "../../../Utilities/Platform/InfiniteLoading/InfiniteLoader";
import InfiniteLoading from "../../../Utilities/Platform/InfiniteLoading/InfiniteLoading";
import BodyLargeList from "../../../Utilities/Platform/LargeList/BodyLargeList";
import LargeList from "../../../Utilities/Platform/LargeList/LargeList";
import { type RawReportData } from "../../../Utilities/Platform/Reports";
import UnescapedContent from "../../Misc/UnescapedContent";
import { type ReportGroupData, type ReportTableResults } from "./ReportTable";
import ReportTableRow from "./ReportTableRow";

interface Props {
    onLoad: () => Promise<void>;
    data: Array<ReportTableResults>;
    columns: Array<string>;
    complete: boolean;
    maxDepth: number;
    widest?: Record<string, string>;
    groups: ReportGroupData;
    onGroupClick: (group: Array<string>) => void;
    parentElement?: HTMLElement;
    nullName?: string;
    columnClasses: Record<string, string>;
    layers?: number;
    nullGroupNames: Array<string>;
    loading: boolean;
    topPadding?: number;
    height?: number;
}

export default class ReportTableBody extends React.Component<Props> {
    protected infiniteRef: React.RefObject<InfiniteLoader | null>;

    constructor(props: Props) {
        super(props);

        this.infiniteRef = React.createRef();

        this.renderElement = this.renderElement.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
    }

    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if(prevProps.layers !== this.props.layers || prevProps.loading !== this.props.loading) {
            this.infiniteRef.current?.handleScroll();
        }
    }

    protected handleRowClick(type: string, data: RawReportData, groups: Array<string>): void {
        if(type === "group") {
            this.props.onGroupClick([
                ...groups,
                data.name.toString(),
            ]);
        }
    }

    protected renderElement(index: number): React.ReactNode {
        const row = this.props.data[index];

        return (
            <ReportTableRow
                key={row.type + "|" + index}
                type={row.type}
                data={row.data}
                depth={row.depth}
                parentGroups={row.parentGroups}
                columns={this.props.columns}
                groups={this.props.groups}
                onClick={this.handleRowClick}
                nullName={this.props.nullName}
                columnClasses={this.props.columnClasses}
                layers={this.props.layers}
                nullGroupNames={this.props.nullGroupNames}
            />
        );
    }

    public render(): React.ReactNode {
        const widest = this.props.widest;

        return (
            <tbody className="report-table-body">
                {!!widest && (
                    <tr className="report-table-hidden">
                        {this.props.columns.map((column, index) => (
                            <td
                                key={column}
                                style={(index !== 0 ? {} : {
                                    paddingLeft: (this.props.maxDepth * 32 + 10) + "px",
                                })}
                            >
                                <UnescapedContent>
                                    {!widest[column] ? "" : widest[column]}
                                </UnescapedContent>
                            </td>
                        ))}
                    </tr>
                )}
                <InfiniteLoading
                    ref={this.infiniteRef}
                    onLoad={this.props.onLoad}
                    loadingIndicator={<tr><td colSpan={this.props.columns.length}>Loading...</td></tr>}
                    hasNextPage={!this.props.complete}
                >
                    {(typeof this.props.parentElement === "undefined" ? (
                        <BodyLargeList
                            elementHeight={36}
                            hiddenElement={<tr />}
                            renderIndex={this.renderElement}
                            indexCount={this.props.data.length}
                            offset={this.props.topPadding}
                            height={this.props.height}
                        />
                    ) : (
                        <LargeList
                            elementHeight={36}
                            hiddenElement={<tr />}
                            renderIndex={this.renderElement}
                            indexCount={this.props.data.length}
                            element={this.props.parentElement}
                            topPadding={this.props.topPadding}
                            height={this.props.height}
                        />
                    ))}
                </InfiniteLoading>
            </tbody>
        );
    }
}
